import { FC, useEffect, useMemo, useState } from "react";
import { CarDataType } from "data/types";
import TabFilters from "./TabFilters";
import { useLocation, useNavigate } from "react-router-dom";
import { useData } from "data/data-provider";
import ExpandingFleetGrid from "designs/DesignB/components/FleetGrid/ExpandingFleetGrid";
import FleetLayout, { FilterChip } from "designs/DesignB/components/Vehicles/FleetLayout";
import NoCarsMatch from "designs/DesignB/components/Vehicles/NoCarsMatch";
import { fleetCategories } from "designs/DesignB/components/CarLogo/categories";
import { useFleetFilters } from "hooks/useFleetFilters";
import { FLEET_SORTS, FleetSort, sortFleet } from "utils/fleetSort";

export interface SectionGridFilterCardProps {
  className?: string;
  data?: CarDataType[];
}

const PAGE = 8;

const SectionGridFilterCard: FC<SectionGridFilterCardProps> = ({
  className = "",
  data,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { ourVehicles, ourVehiclesLoading, ourVehiclesSettled } = useData();
  const [visibleCarsCount, setVisibleCarsCount] = useState<number>(PAGE); // How many cars are visible
  const queryParams = new URLSearchParams(location.search);
  // 1NOW-11128: category links open /vehicles?class=<name>. `make` is the
  // older spelling of the same link and still works. 1NOW-11193: the page also
  // writes its filters back (below), one `class` per ticked class.
  const classParams = (queryParams.getAll("class").length ? queryParams.getAll("class") : queryParams.getAll("make"))
    .map((name) => name.trim())
    .filter(Boolean);
  const urlSort = queryParams.get("sort");
  const [sort, setSort] = useState<FleetSort>(
    FLEET_SORTS.some((s) => s.value === urlSort) ? (urlSort as FleetSort) : "recommended"
  );
  const urlPrice = (queryParams.get("price") ?? "").match(/^(\d+)-(\d+)$/);
  // 1NOW-11129: class and price are applied together, from one place.
  const {
    types,
    selectedTypes,
    setSelectedTypes,
    rangePrices,
    setRangePrices,
    maxPrice,
    filteredFleet,
    clearFilters,
    resetKey,
  } = useFleetFilters(ourVehicles, classParams, urlPrice ? [Number(urlPrice[1]), Number(urlPrice[2])] : null);
  // 1NOW-11132: the fleet has cars, and the filters leave none.
  const noMatches =
    !ourVehiclesLoading && (ourVehicles?.length ?? 0) > 0 && filteredFleet.length === 0;

  // The link's classes in the fleet's own spelling, so ?class=suv ticks "SUV".
  const urlClasses = useMemo(
    () => classParams.map((name) => types.find((c) => c.toLowerCase() === name.toLowerCase()) ?? name),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [classParams.join("\n"), types]
  );

  // Follow the URL when it changes (a category link while already on
  // /vehicles, or the Vehicles menu item clearing it).
  useEffect(() => {
    if (urlClasses.join("\n") !== selectedTypes.join("\n")) setSelectedTypes(urlClasses);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlClasses]);

  // 1NOW-11193: and write every filter back, so a refresh or a shared link shows
  // what is on screen: Clear all gives plain /vehicles. Replaced, not pushed, so
  // Back leaves the page instead of stepping through each tick.
  const priceMoved = rangePrices[0] > 0 || rangePrices[1] < maxPrice;
  useEffect(() => {
    const next = new URLSearchParams(location.search);
    next.delete("make");
    next.delete("class");
    selectedTypes.forEach((type) => next.append("class", type));
    if (sort === "recommended") next.delete("sort");
    else next.set("sort", sort);
    if (priceMoved) next.set("price", `${rangePrices[0]}-${rangePrices[1]}`);
    else next.delete("price");
    const search = next.toString();
    if (search !== location.search.replace(/^\?/, "")) {
      navigate({ pathname: location.pathname, search: search ? `?${search}` : "" }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTypes, sort, priceMoved, rangePrices[0], rangePrices[1]]);

  // How many cars each class holds, for the sidebar.
  const counts = useMemo(
    () => Object.fromEntries(fleetCategories(ourVehicles).map((c) => [c.name, c.count])),
    [ourVehicles]
  );

  // The cars in the order picked, and the part of them shown so far.
  const sorted = useMemo(() => sortFleet(filteredFleet, sort), [filteredFleet, sort]);
  const carsToDisplay = sorted.slice(0, visibleCarsCount);

  // The active filters, as chips the renter can take off one by one.
  const chips: FilterChip[] = [
    ...selectedTypes.map((type) => ({
      key: `type-${type}`,
      label: type,
      onRemove: () => setSelectedTypes(selectedTypes.filter((t) => t !== type)),
    })),
    ...(priceMoved
      ? [{ key: "price", label: `$${rangePrices[0]} – $${rangePrices[1]}`, onRemove: () => setRangePrices([0, maxPrice]) }]
      : []),
  ];

  return (
    <div
      className={`nc-SectionGridFilterCard ${className}`}
      data-nc-id="SectionGridFilterCard"
      style={{ minHeight: "80vh" }}
    >
      <FleetLayout
        eyebrow="Our fleet"
        title="All vehicles"
        count={ourVehiclesLoading || !ourVehicles ? undefined : filteredFleet.length}
        sort={sort}
        onSort={setSort}
        chips={chips}
        onClearAll={clearFilters}
        filters={
          ourVehicles && (
            <TabFilters
              key={resetKey}
              types={types}
              selectedTypes={selectedTypes}
              setSelectedTypes={setSelectedTypes}
              rangePrices={rangePrices}
              setRangePrices={setRangePrices}
              maxPrice={maxPrice}
              counts={counts}
            />
          )
        }
      >
        <div className="nc-SectionGridFeaturePlaces relative">
          {/* Show a loader while the fleets fetch is in progress so the page
              doesn't look blank/broken after the Book Now CTA navigates here
              (1NOW-1265). Mirrors the Design B home grid's loading state. */}
          {ourVehiclesLoading ? (
            <div className="flex justify-center items-center">
              <div className="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full border-t-transparent border-secondary-text-color"></div>
            </div>
          ) : noMatches ? (
            <NoCarsMatch onClear={clearFilters} />
          ) : (
            // 1NOW-11219: a card opens the car's details in place; Book now in there goes on to
            // the car page, as the card's own button used to.
            <ExpandingFleetGrid
              cars={carsToDisplay}
              gridClassName="sm:grid-cols-2 xl:grid-cols-3"
              resetKey={`${selectedTypes.join("|")}/${rangePrices.join("-")}/${sort}`}
              pool={sorted}
              onReveal={(n) => setVisibleCarsCount((count) => Math.max(count, Math.ceil(n / PAGE) * PAGE))}
              settled={!ourVehiclesLoading && ourVehiclesSettled !== false}
              onBook={(car) => navigate("/car-listing-detail?selectedCarId=" + car.id)}
            />
          )}

          {/* How far down the list the renter is, and the way on (or back). */}
          {!ourVehiclesLoading && sorted.length > PAGE && (
            <div className="mt-10 flex flex-col items-center gap-4">
              <p className="text-[13px] font-semibold text-secondary-text-color">
                Showing {carsToDisplay.length} of {sorted.length}
              </p>
              {sorted.length > visibleCarsCount ? (
                <button
                  type="button"
                  className="db-btn db-btn--ghost"
                  onClick={() => setVisibleCarsCount((count) => count + PAGE)}
                >
                  Load More
                </button>
              ) : (
                <button type="button" className="db-btn db-btn--ghost" onClick={() => setVisibleCarsCount(PAGE)}>
                  Load Less
                </button>
              )}
            </div>
          )}
        </div>
      </FleetLayout>
    </div>
  );
};

export default SectionGridFilterCard;
